import {
  InputSelectControl,
  SettingsFormControl,
  SettingsGroup,
  TextInputField
} from '@sequencehq/core-components'
import { MerchantBankAccountDetails } from '../bankDetails.types'
import { BASE_INPUT_FIELD_STYLES } from 'SettingsV2/settings.constants'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import {
  CountriesAlpha2,
  countriesAlpha2,
  State,
  usStates
} from '@sequencehq/api'

export const useUsWireAccountForm = ({
  bankDetails,
  showValidationErrors
}: {
  bankDetails?: MerchantBankAccountDetails['value']
  showValidationErrors: boolean
}) => {
  return useForm({
    value: {
      accountName: bankDetails?.usWireDestination?.accountName ?? '',
      accountNumber: bankDetails?.usWireDestination?.accountNumber ?? '',
      bankName: bankDetails?.usWireDestination?.bankName ?? '',
      bankRoutingNumber:
        bankDetails?.usWireDestination?.bankRoutingNumber ?? '',
      swiftCode: bankDetails?.usWireDestination?.swiftCode ?? '',
      country: bankDetails?.usWireDestination?.bankAddress.country,
      line1: bankDetails?.usWireDestination?.bankAddress.line1 ?? '',
      line2: bankDetails?.usWireDestination?.bankAddress.line2,
      postcode: bankDetails?.usWireDestination?.bankAddress.postcode ?? '',
      state: bankDetails?.usWireDestination?.bankAddress.state,
      town: bankDetails?.usWireDestination?.bankAddress.town ?? ''
    },
    showValidationErrors,
    fieldConfiguration: [
      {
        property: 'accountName',
        validation: [required]
      },
      {
        property: 'accountNumber',
        validation: [required]
      },
      {
        property: 'bankName',
        validation: [required]
      },
      {
        property: 'bankRoutingNumber',
        validation: [required]
      },
      {
        property: 'swiftCode',
        validation: [required]
      },
      {
        property: 'country',
        validation: [required]
      },
      {
        property: 'line1',
        validation: [required]
      },
      {
        property: 'line2',
        validation: []
      },
      {
        property: 'postcode',
        validation: [required]
      },
      {
        property: 'state',
        validation: []
      },
      {
        property: 'town',
        validation: [required]
      }
    ]
  })
}

export const UsWireAccountForm = ({
  fields
}: {
  fields: ReturnType<typeof useUsWireAccountForm>['fields']
}) => {
  const resetBankAddress = () => {
    fields.line1.onChange('')
    fields.line2.onChange('')
    fields.postcode.onChange('')
    fields.state.onChange(undefined)
    fields.town.onChange('')
  }
  return (
    <>
      <SettingsGroup title="Account details">
        <SettingsFormControl label="Account name" htmlFor="account-name">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="John Doe"
            id="account-name"
            data-testid="bankDetails.accountName"
            value={fields.accountName.value}
            validationErrors={fields.accountName.validationErrors}
            onChange={e => {
              fields.accountName.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="Account number" htmlFor="account-number">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="12345678"
            id="account-number"
            value={fields.accountNumber.value}
            validationErrors={fields.accountNumber.validationErrors}
            onChange={e => {
              fields.accountNumber.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="Bank name" htmlFor="bank-name">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder=""
            id="bank-name"
            value={fields.bankName.value}
            validationErrors={fields.bankName.validationErrors}
            onChange={e => {
              fields.bankName.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl
          label="Bank routing number"
          htmlFor="bank-routing-number"
        >
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder="123456789"
            id="bank-routing-number"
            value={fields.bankRoutingNumber.value}
            validationErrors={fields.bankRoutingNumber.validationErrors}
            onChange={e => {
              fields.bankRoutingNumber.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>

        <SettingsFormControl label="SWIFT code" htmlFor="swift-code">
          <TextInputField
            {...BASE_INPUT_FIELD_STYLES}
            placeholder=""
            id="swift-code"
            value={fields.swiftCode.value}
            validationErrors={fields.swiftCode.validationErrors}
            onChange={e => {
              fields.swiftCode.onChange(e.target.value)
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>
      <SettingsGroup title="Bank address">
        <SettingsFormControl label="Country" htmlFor="country">
          <InputSelectControl
            id="Country"
            placeholder="Choose a country"
            triggerStyle={{
              width: BASE_INPUT_FIELD_STYLES.width
            }}
            options={Object.entries(countriesAlpha2).map(([key, label]) => ({
              value: key,
              label: label
            }))}
            initialValue={fields.country.value}
            onChange={value => {
              fields.country.onChange(value as CountriesAlpha2)
              resetBankAddress()
            }}
          />
        </SettingsFormControl>

        {fields.country.value && (
          <>
            <SettingsFormControl
              label="Address Line 1"
              htmlFor="address-line-1"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="123 Main Street"
                id="address-line-1"
                value={fields.line1.value}
                validationErrors={fields.line1.validationErrors}
                onChange={e => {
                  fields.line1.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl
              label="Address Line 2 (Optional)"
              htmlFor="address-line-2"
            >
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="Apt, Suite, Unit, Building (optional)"
                id="address-line-2"
                value={fields.line2.value}
                validationErrors={fields.line2.validationErrors}
                onChange={e => {
                  fields.line2.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            <SettingsFormControl label="City" htmlFor="city">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="New York"
                id="city"
                value={fields.town.value}
                validationErrors={fields.town.validationErrors}
                onChange={e => {
                  fields.town.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>

            {fields.country.value === 'US' && (
              <SettingsFormControl label="State" htmlFor="state">
                <InputSelectControl
                  id="state"
                  options={Object.entries(usStates).map(([key, label]) => ({
                    value: key,
                    label: label
                  }))}
                  placeholder="Choose a state"
                  triggerStyle={{
                    width: BASE_INPUT_FIELD_STYLES.width
                  }}
                  initialValue={fields.state.value}
                  onChange={value => {
                    fields.state.onChange(value as State)
                  }}
                />
              </SettingsFormControl>
            )}

            <SettingsFormControl label="ZIP" htmlFor="postcode">
              <TextInputField
                {...BASE_INPUT_FIELD_STYLES}
                placeholder="10001"
                id="postcode"
                value={fields.postcode.value}
                validationErrors={fields.postcode.validationErrors}
                onChange={e => {
                  fields.postcode.onChange(e.target.value)
                }}
              />
            </SettingsFormControl>
          </>
        )}
      </SettingsGroup>
    </>
  )
}

export const formatUsWireFormData = (
  formData: ReturnType<typeof useUsWireAccountForm>['queries']['formData']
) => {
  const { country, line1, line2, state, town, postcode, ...rest } = formData
  return {
    ...rest,
    bankAddress: {
      country: country as CountriesAlpha2,
      line1,
      line2,
      state,
      town,
      postcode
    }
  }
}
